// Comparison utilities for location-based analysis and ranking

import {
  calculateServiceCoverage,
  calculateInfrastructureReadiness,
  servicesList,
  infrastructureList,
} from './chartUtils'

// Determine comparison targets based on current location selection
export function getComparisonTargets(
  locations,
  selectedDistrict,
  selectedSubcounty,
  selectedParish,
  selectedVillage,
  maxDepth = 'village' // Add maxDepth parameter with default
) {
  if (!locations) return { targets: [], type: 'none', context: '' }

  // Find the selected district data
  const districtData = locations.districts?.find(d => d.id === selectedDistrict)
  if (!districtData) return { targets: [], type: 'none', context: '' }

  // Helper function to check if a depth is allowed
  const isDepthAllowed = depth => {
    const depths = ['district', 'subcounty', 'parish', 'village']
    const maxIndex = depths.indexOf(maxDepth)
    const currentIndex = depths.indexOf(depth)
    return currentIndex <= maxIndex
  }

  // Village selected → Show individual facilities (no sub-locations to compare)
  // But only if village depth is allowed
  if (selectedVillage && isDepthAllowed('village')) {
    return {
      targets: [],
      type: 'facilities',
      context: `All facilities in ${selectedVillage}`,
      level: 'village',
    }
  }

  // Parish selected → Compare Villages (if village depth is allowed)
  if (selectedParish && isDepthAllowed('village')) {
    const subcountyData = districtData.subcounties?.find(
      s => s.id === selectedSubcounty
    )
    const parishData = subcountyData?.parishes?.find(
      p => p.id === selectedParish
    )

    return {
      targets: parishData?.villages || [],
      type: 'villages',
      context: `Villages in ${parishData?.name || selectedParish}`,
      level: 'village',
    }
  }

  // If parish is selected but village depth is not allowed, show facilities
  if (selectedParish && !isDepthAllowed('village')) {
    return {
      targets: [],
      type: 'facilities',
      context: `All facilities in ${selectedParish}`,
      level: 'parish',
    }
  }

  // Subcounty selected → Compare Parishes (if parish depth is allowed)
  if (selectedSubcounty && isDepthAllowed('parish')) {
    const subcountyData = districtData.subcounties?.find(
      s => s.id === selectedSubcounty
    )

    return {
      targets: subcountyData?.parishes || [],
      type: 'parishes',
      context: `Parishes in ${subcountyData?.name || selectedSubcounty}`,
      level: 'parish',
    }
  }

  // If subcounty is selected but parish depth is not allowed, show facilities
  if (selectedSubcounty && !isDepthAllowed('parish')) {
    return {
      targets: [],
      type: 'facilities',
      context: `All facilities in ${selectedSubcounty}`,
      level: 'subcounty',
    }
  }

  // District selected → Compare Subcounties (if subcounty depth is allowed)
  if (selectedDistrict && isDepthAllowed('subcounty')) {
    return {
      targets: districtData.subcounties || [],
      type: 'subcounties',
      context: `Subcounties in ${districtData.name}`,
      level: 'subcounty',
    }
  }

  // If district is selected but subcounty depth is not allowed, show facilities
  if (selectedDistrict && !isDepthAllowed('subcounty')) {
    return {
      targets: [],
      type: 'facilities',
      context: `All facilities in ${districtData.name}`,
      level: 'district',
    }
  }

  return { targets: [], type: 'none', context: '' }
}

// Group facilities by location for comparison
export function groupFacilitiesByLocation(facilities, targets, level) {
  const grouped = {}

  // Initialize groups for all targets
  targets.forEach(target => {
    grouped[target.id] = {
      id: target.id,
      name: target.name,
      code: target.code,
      facilities: [],
    }
  })

  // Group facilities by their location codes
  facilities.forEach(facility => {
    if (!facility.location_code) return

    // Find which target this facility belongs to
    const targetId = findLocationTarget(facility.location_code, targets, level)
    if (targetId && grouped[targetId]) {
      grouped[targetId].facilities.push(facility)
    }
  })

  return Object.values(grouped).filter(group => group.facilities.length > 0)
}

// Find which target location a facility belongs to based on location code
function findLocationTarget(facilityLocationCode, targets, _level) {
  return targets.find(target => {
    if (!target.code || !facilityLocationCode) return false
    return facilityLocationCode.startsWith(target.code)
  })?.id
}

// Calculate comprehensive stats for each location
export function calculateLocationStats(locationGroup) {
  const facilities = locationGroup.facilities
  if (!facilities.length) {
    return {
      id: locationGroup.id,
      name: locationGroup.name,
      facilityCount: 0,
      serviceCoverage: [],
      infrastructureReadiness: [],
      facilityLevelMix: {},
      topGaps: [],
      compositeScore: 0,
    }
  }

  // Calculate service coverage
  const serviceCoverage = calculateServiceCoverage(facilities)

  // Calculate infrastructure readiness
  const infrastructureReadiness = calculateInfrastructureReadiness(facilities)

  // Calculate facility level distribution
  const facilityLevelMix = {}
  facilities.forEach(facility => {
    const level = facility.level || 'Unknown'
    facilityLevelMix[level] = (facilityLevelMix[level] || 0) + 1
  })

  // Identify top 3 service gaps (lowest coverage)
  const topGaps = serviceCoverage
    .filter(service => service.percentage < 70)
    .sort((a, b) => a.percentage - b.percentage)
    .slice(0, 3)
    .map(service => ({
      name: service.name,
      percentage: service.percentage,
      severity:
        service.percentage < 30
          ? 'critical'
          : service.percentage < 50
            ? 'high'
            : 'moderate',
    }))

  // Calculate composite score (weighted average of key metrics)
  const avgServiceCoverage =
    serviceCoverage.reduce((sum, s) => sum + s.percentage, 0) /
    serviceCoverage.length
  const avgInfrastructure =
    infrastructureReadiness.reduce((sum, i) => sum + i.percentage, 0) /
    infrastructureReadiness.length
  const facilityDensityScore = Math.min(facilities.length * 10, 100) // Cap at 100, 10 points per facility

  const compositeScore = Math.round(
    avgServiceCoverage * 0.4 +
      avgInfrastructure * 0.3 +
      facilityDensityScore * 0.3
  )

  return {
    id: locationGroup.id,
    name: locationGroup.name,
    facilityCount: facilities.length,
    serviceCoverage,
    infrastructureReadiness,
    facilityLevelMix,
    topGaps,
    compositeScore,
    avgServiceCoverage: Math.round(avgServiceCoverage),
    avgInfrastructure: Math.round(avgInfrastructure),
  }
}

// Generate comparison rankings with multiple sorting options
export function generateLocationRankings(
  locationStats,
  sortBy = 'compositeScore',
  sortOrder = 'desc'
) {
  const sorted = [...locationStats].sort((a, b) => {
    let aValue = a[sortBy]
    let bValue = b[sortBy]

    // Handle different value types
    if (typeof aValue === 'undefined') aValue = 0
    if (typeof bValue === 'undefined') bValue = 0

    const comparison = sortOrder === 'desc' ? bValue - aValue : aValue - bValue

    // Tie breaker: alphabetical by name
    if (comparison === 0) {
      return a.name.localeCompare(b.name)
    }

    return comparison
  })

  // Add rankings
  return sorted.map((location, index) => ({
    ...location,
    rank: index + 1,
    percentileRank: Math.round(((sorted.length - index) / sorted.length) * 100),
  }))
}

// Create service coverage heatmap data
export function createServiceCoverageHeatmap(locationStats) {
  if (!locationStats.length) return { locations: [], services: [], data: [] }

  const locations = locationStats.map(stat => stat.name)
  const services = servicesList.slice(0, 6) // Top 6 most important services

  const data = []
  locationStats.forEach((location, locationIndex) => {
    services.forEach((service, serviceIndex) => {
      const serviceData = location.serviceCoverage.find(
        s => s.name === service.name
      )
      const percentage = serviceData ? serviceData.percentage : 0

      // Determine color based on percentage
      let color = '#cd2b2a' // Red for poor coverage
      if (percentage >= 80)
        color = '#3b8c62' // Green for good coverage
      else if (percentage >= 50) color = '#f7a838' // Yellow for moderate coverage

      data.push({
        locationIndex,
        serviceIndex,
        locationName: location.name,
        serviceName: service.name,
        percentage,
        color,
        status:
          percentage >= 80 ? 'good' : percentage >= 50 ? 'moderate' : 'poor',
      })
    })
  })

  return { locations, services, data }
}

// Create infrastructure readiness heatmap
export function createInfrastructureHeatmap(locationStats) {
  if (!locationStats.length)
    return { locations: [], infrastructure: [], data: [] }

  const locations = locationStats.map(stat => stat.name)
  const infrastructure = infrastructureList.slice(0, 6) // Top 6 most important infrastructure

  const data = []
  locationStats.forEach((location, locationIndex) => {
    infrastructure.forEach((infra, infraIndex) => {
      const infraData = location.infrastructureReadiness.find(
        i => i.name === infra.name
      )
      const percentage = infraData ? infraData.percentage : 0

      let color = '#cd2b2a'
      if (percentage >= 80) color = '#3b8c62'
      else if (percentage >= 50) color = '#f7a838'

      data.push({
        locationIndex,
        infraIndex,
        locationName: location.name,
        infraName: infra.name,
        percentage,
        color,
        status:
          percentage >= 80 ? 'good' : percentage >= 50 ? 'moderate' : 'poor',
      })
    })
  })

  return { locations, infrastructure, data }
}

// Chart options for comparison visualizations
export function createCapacityRankingOption(rankings, title) {
  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const item = params[0]
        const data = rankings[item.dataIndex]
        return `
          <b>${data.name}</b><br/>
          Rank: #${data.rank}<br/>
          Facilities: ${data.facilityCount}<br/>
          Composite Score: ${data.compositeScore}
        `
      },
    },
    grid: {
      left: '10%',
      right: '20%',
      top: '5%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      name: 'Number of Facilities',
      nameTextStyle: {
        fontSize: 12,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      data: rankings.map(r => r.name),
      axisLabel: {
        fontSize: 12,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Facilities',
        type: 'bar',
        data: rankings.map(r => ({
          value: r.facilityCount,
          itemStyle: {
            color:
              r.rank <= 3 ? '#10b981' : r.rank <= 6 ? '#3b82f6' : '#6b7280',
          },
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}',
          fontSize: 11,
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
      },
    ],
  }
}

export function createServiceComparisonOption(locationStats, title) {
  const topServices = servicesList.slice(0, 4) // Top 4 services
  const locations = locationStats.map(stat => stat.name)

  const series = topServices.map((service, index) => {
    const colors = ['#ef233c', '#c10e66', '#7f2a74', '#3b2f65']

    return {
      name: service.name,
      type: 'bar',
      data: locationStats.map(location => {
        const serviceData = location.serviceCoverage.find(
          s => s.name === service.name
        )
        return serviceData ? serviceData.percentage : 0
      }),
      itemStyle: { color: colors[index] },
      barGap: 0,
    }
  })

  return {
    title: {
      show: false,
      text: title,
      left: 'center',
      top: 20,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        let result = `<b>${params[0].name}</b><br/>`
        params.forEach(param => {
          result += `${param.seriesName}: ${param.value}%<br/>`
        })
        return result
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    legend: {
      top: 0,
      left: 'center',
      textStyle: {
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
    },
    grid: {
      left: '15%',
      right: '10%',
      top: '15%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: locations,
      axisLabel: {
        fontSize: 12,
        interval: 0,
        rotate: locations.length > 4 ? 30 : 0,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      name: 'Coverage %',
      nameTextStyle: { fontSize: 12 },
      axisLabel: {
        formatter: '{value}%',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    series,
  }
}
